import React, { useState, useRef } from 'react';
import { X, Link as LinkIcon, Image, FileText, Upload } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface AttachmentDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAttach: (attachment: {
    type: 'link' | 'image' | 'file';
    url: string;
    title?: string;
    description?: string;
    thumbnailUrl?: string;
  }) => void;
  type: 'link' | 'image' | 'file';
}

function AttachmentDialog({ isOpen, onClose, onAttach, type }: AttachmentDialogProps) {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (type === 'link') {
      if (!url.trim()) {
        setError('Please enter a valid URL');
        return;
      }

      try {
        new URL(url); // Validate URL format
        onAttach({
          type,
          url: url.trim(),
          title: title.trim() || undefined,
          description: description.trim() || undefined
        });
        onClose();
      } catch {
        setError('Please enter a valid URL');
      }
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      setError(null);
      const file = event.target.files?.[0];
      if (!file) return;

      // Validate file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size should be less than 5MB');
        return;
      }

      // Validate file type for images
      if (type === 'image' && !file.type.startsWith('image/')) {
        setError('Please upload an image file');
        return;
      }

      setUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${type === 'image' ? 'images' : 'files'}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('post-attachments')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('post-attachments')
        .getPublicUrl(filePath);

      onAttach({
        type,
        url: publicUrl,
        title: file.name
      });
      onClose();
    } catch (error) {
      console.error('Error uploading file:', error);
      setError('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg w-full max-w-md">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-semibold flex items-center gap-2">
            {type === 'link' && <LinkIcon className="h-5 w-5" />}
            {type === 'image' && <Image className="h-5 w-5" />}
            {type === 'file' && <FileText className="h-5 w-5" />}
            Add {type}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {type === 'link' ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  URL
                </label>
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder={`Enter ${type} URL`}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title (optional)
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Enter title"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description (optional)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Enter description"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                  rows={3}
                />
              </div>

              <button
                type="submit"
                className="w-full py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
              >
                Add {type}
              </button>
            </form>
          ) : (
            <div className="text-center">
              <div className="mt-2 flex justify-center">
                <div
                  className="w-32 h-32 border-2 border-gray-300 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-indigo-500 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="h-8 w-8 text-gray-400" />
                  <p className="mt-2 text-sm text-gray-500">
                    Click to upload {type}
                  </p>
                  <p className="mt-1 text-xs text-gray-400">
                    Max size: 5MB
                  </p>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept={type === 'image' ? 'image/*' : undefined}
                  className="hidden"
                />
              </div>

              {uploading && (
                <div className="mt-4 flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                  <span className="text-sm text-gray-600">Uploading...</span>
                </div>
              )}
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600 text-center">{error}</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default AttachmentDialog;